import React, { useCallback } from 'react';
import { Button } from '@packages/ui/primitives';
import { useConfirm } from '@packages/ui/composites';
import { palette } from '@packages/ui/tokens';
import type { FormEachNode } from '../../../types/layout';
import { resolveRemoveButtonVariant } from '../../../utils/eachButtonVariants';
import { resolveRemovePresentation } from '../../../utils/eachConstraints';

interface FormEachRemoveButtonProps {
  node: FormEachNode;
  index: number;
  onRemove: (index: number) => void;
}

export const FormEachRemoveButton = React.memo(function FormEachRemoveButton({
  node,
  index,
  onRemove,
}: FormEachRemoveButtonProps): React.ReactElement | null {
  const { confirm } = useConfirm();
  const presentation = resolveRemovePresentation(node);

  if (presentation === 'hidden') {
    return null;
  }

  const confirmConfig = node.removeButton?.confirm;
  const icon = node.removeButton?.icon ?? (presentation === 'inline-icon' ? 'close' : 'delete');
  const label = presentation === 'inline-icon'
    ? undefined
    : (node.removeButton?.label ?? 'Remove');

  const handleClick = useCallback(async () => {
    if (confirmConfig) {
      const props = typeof confirmConfig === 'object' ? confirmConfig : {};
      const { confirmed } = await confirm({
        title: props.title ?? 'Remove this item?',
        description: props.message,
        variant: 'delete',
        preset: true,
      });
      if (!confirmed) return;
    }
    onRemove(index);
  }, [confirm, confirmConfig, index, onRemove]);

  const isInlineIcon = presentation === 'inline-icon';

  return (
    <Button
      variant={resolveRemoveButtonVariant(node.removeButton?.variant ?? presentation)}
      size={isInlineIcon ? 'md' : 'sm'}
      icon={icon}
      label={label}
      onClick={() => void handleClick()}
      type='button'
      semanticId={`${node.name}.${index}.remove`}
      {...(isInlineIcon && { fontColor: palette.red[600] })}
    />
  );
});
